import React, { useCallback, useState } from 'react';
import { ActivityIndicator, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCheckCircle, faCircleInfo, faUserGroup, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faCalendar, faClock, faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import MyAppText from '../../../../components/MyAppText';
import { translate } from '../../../../services/translate';
import { Tabs } from '../../../../components/Tabs';
import CustomButton from '../../../../components/CustomButton';
import { ScrollView } from 'react-native-web-hover';
import moment from 'moment';
import Badge from '../../../../components/Badge';
import { DEFAULT_PASTEL_COLORS, DEFAULT_PASTEL_TEXT_COLORS } from '../../../../styles/colorPalettes';
import Tooltip from '../../../../components/Tooltip';
import VacancyApplyModal from './VacancyApplyModal';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { appAgentService, InvalidPermissionToVacancy } from '../../../../services/central-api/app-agent';
import Toast from 'react-native-toast-message';
import Clipboard from '@react-native-clipboard/clipboard';
import { useAuth } from '../../../../context/Auth';

export default function VacancyDetailsApply({ navigation, route }: {
    navigation: Navigation;
    route: {
        params?: {
            id: number;
        };
    };
}) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
    const [vacancy, setVacancy] = useState<Vacancy | undefined>(undefined);
    const [application, setApplication] = useState<Application | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const userInVacation = vacancy && authenticatedUser?.vacationPeriodStart &&
        authenticatedUser.vacationPeriodEnd &&
        moment(vacancy.activityStart).isBetween(authenticatedUser.vacationPeriodStart, authenticatedUser.vacationPeriodEnd, null, '[]');

    const userInLeave = vacancy && authenticatedUser?.leaveOfAbsenceStart &&
        authenticatedUser.leaveOfAbsenceEnd &&
        moment(vacancy.activityStart).isBetween(authenticatedUser.leaveOfAbsenceStart, authenticatedUser.leaveOfAbsenceEnd, null, '[]');

    async function getVacancy() {
        try {
            setIsLoading(true);
            const response = await appAgentService.getVacancy(route.params?.id as number);
            setVacancy(response.vacancy);
            setApplication(response.application);
        } catch (err) {
            if (err instanceof InvalidPermissionToVacancy) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate('invalidPermissionToVacancy'),
                });
                return navigation.navigate('OpenVacancies');
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getVacancy();
        }
    }, [isFocused, navigation, route.params]));

    function goBack() {
        navigation.canGoBack() ? navigation.goBack() : navigation.navigate('OpenVacancies');
    }

    return (
        <View style={styles.container(UnistylesRuntime.themeName == 'dark')}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <FontAwesomeIcon
                    icon={faArrowLeft}
                    color={theme.colors.iconColor}
                    fontSize={20}
                    cursor='pointer'
                    onClick={() => goBack()}
                />
            </View>
            <View style={styles.content}>
                {isLoading
                    ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                    </View>
                    : !vacancy
                        ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                            <MyAppText style={[styles.vacanciesText, { textAlign: 'center' }]}>{translate('invalidPermissionToVacancy')}</MyAppText>
                        </View>
                        : <>
                            <View style={styles.contentHeader}>
                                <View style={{ flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                                    <MyAppText style={styles.activityTitle}>{vacancy.title}</MyAppText>
                                    <View style={{ flexDirection: 'row', columnGap: 2 }}>
                                        <FontAwesomeIcon icon={faUserGroup} fontSize={17} color={theme.colors.neutral['400']} />
                                        <MyAppText style={styles.vacanciesText}>{vacancy.quantity} {translate('vacancies').toLowerCase()}</MyAppText>
                                    </View>

                                </View>
                                <TouchableOpacity onPress={() => {
                                    Clipboard.setString(window.location.href);
                                    Toast.show({
                                        type: 'sentinelInfo',
                                        text1: translate('copiedLink')
                                    });
                                }}>
                                    <FontAwesomeIcon
                                        icon={faShareNodes as IconDefinition}
                                        color={theme.colors.primary['500']}
                                        fontSize={20}
                                        cursor='pointer'
                                    />
                                </TouchableOpacity>
                            </View>
                            <Tabs
                                tabs={[{
                                    key: 'vacancyInfo',
                                    label: translate('vacancyInfo'),
                                }, {
                                    key: 'requirements',
                                    label: translate('requirements'),
                                }]}
                            >
                                {({ selectedTab }) => {
                                    if (selectedTab == 'vacancyInfo') {
                                        return <View style={styles.vacancyInfo}>
                                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 16 }}>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('workplace')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{vacancy.workplace.name}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('unit')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{vacancy.workplace.actingBodyUnit.name}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('place')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{vacancy.workplace.address}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('activityDescription')}</MyAppText>
                                                    <MyAppText style={styles.vacancyInfoItemDescription}>{vacancy.description}</MyAppText>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('activityDate')}</MyAppText>
                                                    <View style={styles.iconWithText}>
                                                        <FontAwesomeIcon
                                                            icon={faCalendar as IconDefinition}
                                                            color={theme.colors.neutral['400']}
                                                            fontSize={16}
                                                            cursor='pointer'
                                                        />
                                                        <MyAppText style={styles.vacancyInfoItemDescription}>{moment(vacancy.activityStart).format('DD/MM')}</MyAppText>
                                                    </View>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('activityHours')}</MyAppText>
                                                    <View style={styles.iconWithText}>
                                                        <FontAwesomeIcon
                                                            icon={faClock as IconDefinition}
                                                            color={theme.colors.neutral['400']}
                                                            fontSize={16}
                                                            cursor='pointer'
                                                        />

                                                        <MyAppText style={styles.vacancyInfoItemDescription}>{moment(vacancy.activityStart).format('HH:mm')} - {moment(vacancy.activityFinish).format('HH:mm')}</MyAppText>
                                                    </View>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <MyAppText style={styles.vacancyInfoItemTitle}>{translate('levelAndTags')}</MyAppText>
                                                    <View style={{ flexDirection: 'column', gap: 16 }}>
                                                        <View style={{ flexDirection: 'column' }}>
                                                            <MyAppText style={styles.vacancyInfoItemSubTitle}>{translate('level')}:</MyAppText>
                                                            <MyAppText style={styles.vacancyInfoItemDescription}>{vacancy.levels.map(l => l.name).join(', ')}</MyAppText>
                                                        </View>
                                                        <View style={{ flexDirection: 'column' }}>
                                                            <MyAppText style={styles.vacancyInfoItemSubTitle}>{translate('tags')}:</MyAppText>
                                                            <ScrollView style={{ maxHeight: 80 }} contentContainerStyle={{ flexDirection: 'row', columnGap: 8, rowGap: 4, flexWrap: 'wrap' }}>
                                                                {vacancy.tags?.map((item) => {
                                                                    const index = DEFAULT_PASTEL_COLORS.indexOf(item.color);
                                                                    return <Badge
                                                                        mode='ROUNDED'
                                                                        key={item.id}
                                                                        backgroundColor={item.color}
                                                                        textColor={DEFAULT_PASTEL_TEXT_COLORS[index]}
                                                                    >
                                                                        {item.name}
                                                                    </Badge>;
                                                                })}
                                                            </ScrollView>
                                                        </View>

                                                    </View>
                                                </View>
                                                <View style={styles.vacancyInfoItem}>
                                                    <View style={{ zIndex: 2, flexDirection: 'row', columnGap: 8 }}>
                                                        <MyAppText style={styles.vacancyInfoItemTitle}>{translate('applicationCancellation')}</MyAppText>
                                                        <Tooltip
                                                            useSimpleBox
                                                            style={{
                                                                color: theme.colors.white['500'],
                                                                padding: 16,
                                                                borderRadius: 12,
                                                                width: 150,
                                                                justifyContent: 'center',
                                                                zIndex: 9999,
                                                                background: theme.colors.neutral['500'],
                                                            }}
                                                            tooltipContent={translate('applicationCancellationHintMessage')}
                                                            tooltipDirection='left'
                                                        >
                                                            <FontAwesomeIcon icon={faCircleInfo} fontSize={14} color={theme.colors.neutral['500']} />
                                                        </Tooltip>
                                                    </View>
                                                    <MyAppText
                                                        style={styles.vacancyInfoItemDescriptionDanger}
                                                    >
                                                        {translate('cancellationAvailableUntil')} {vacancy.applicationsFinish ? moment(vacancy.applicationsFinish).format('DD/MM/YYYY HH:mm') : moment(vacancy.activityStart).format('DD/MM/YYYY HH:mm')}
                                                    </MyAppText>
                                                </View>
                                            </ScrollView>
                                        </View>;
                                    } else if (selectedTab == 'requirements') {
                                        return <View style={styles.requirements}>
                                            <MyAppText style={styles.activityRequirementsText}>{translate('activityRequirements')}</MyAppText>
                                            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ gap: 8 }}>
                                                {vacancy.skills.map(skill =>
                                                    <View key={skill.id} style={styles.iconWithText}>
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            color={theme.colors.successes['300']}
                                                            fontSize={20}
                                                            cursor='pointer'
                                                            onClick={() => { }}
                                                        />
                                                        <MyAppText style={styles.requirementText}>{skill.name}</MyAppText>

                                                    </View>
                                                )}
                                            </ScrollView>
                                        </View>;
                                    }
                                }}
                            </Tabs>
                        </>}
                <View>
                    {!isLoading && <>
                        {(userInVacation || userInLeave || application)
                            ? (userInVacation || userInLeave)
                                ? <View style={{ flexDirection: 'column', justifyContent: 'center' }}>
                                    <MyAppText style={[styles.vacancyInfoItemDescriptionDanger, { textAlign: 'center' }]}>{translate('cannotApplyDueToLeaveOrVacationPeriod')}</MyAppText>
                                </View>
                                : <View style={{ flexDirection: 'column', justifyContent: 'center' }}>
                                    <MyAppText style={[styles.vacancyInfoItemDescriptionDanger, { textAlign: 'center' }]}>{translate('cannotApplyDueToPeriodOverlay')}:</MyAppText>
                                    <TouchableOpacity
                                        onPress={() => {
                                            navigation.navigate('VacancyDetailsCancel', { id: application?.id });
                                        }}
                                    >
                                        <MyAppText style={[styles.vacancyInfoItemDescriptionDanger, { textDecorationLine: 'underline', textAlign: 'center' }]}>{application?.vacancy.title}</MyAppText>
                                    </TouchableOpacity>
                                </View>
                            : <CustomButton
                                text={translate('applyToVacancy')}
                                onPress={() => setIsConfirmModalOpen(true)}
                                type='primary'
                                disabled={isLoading || !vacancy}
                            />
                        }
                    </>}
                </View>
            </View>
            {vacancy && <VacancyApplyModal
                isModalVisible={isConfirmModalOpen}
                setModalVisible={setIsConfirmModalOpen}
                vacancy={vacancy}
                onConfirm={() => navigation.navigate('OpenVacancies')}
            />
            }
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: (isDark: boolean) => ({
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: isDark ? theme.colors.paginationBar.background : theme.colors.backgrounds['BG-2'],
        flex: 1,
        padding: 16,
        gap: 8,
    }),
    content: {
        flex: 1,
        height: 'auto',
        gap: 16,
    },
    contentHeader: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
    },
    activityTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    vacanciesText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    requirements: {
        flex: 1,
        flexDirection: 'column',
        gap: 8,
    },
    activityRequirementsText: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.primary['500']
    },
    requirementText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    vacancyInfo: {
        flex: 1,
        flexDirection: 'column',
    },
    vacancyInfoItem: {
        flexDirection: 'column',
        gap: 4,
    },
    vacancyInfoItemTitle: {
        fontSize: 16,
        fontWeight: '700',
        color: theme.colors.primary['500']
    },
    vacancyInfoItemSubTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: theme.colors.neutral['500']
    },
    vacancyInfoItemDescription: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    vacancyInfoItemDescriptionDanger: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.errors['300']
    },
    iconWithText: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    }
}));
